/**
 * Marketing configuration: channels, merge fields, audience filters, journey
 * step types and the proven sequences.
 *
 * As with the rest of `constants/`, this is data rather than behaviour — the
 * logic that reads it lives in `domain/marketing.js`.
 */

/** [key, label, icon, accent] */
export const CHANNELS = [
  ['email', 'Email', 'mail', 'g-blue'],
  ['whatsapp', 'WhatsApp', 'chat', 'g-green'],
  ['sms', 'SMS', 'sms', 'g-amber'],
  ['push', 'Push', 'bell', 'g-purple']
];

export const channelLabel = k => (CHANNELS.find(c => c[0] === k) || ['', '—'])[1];
export const channelIcon = k => (CHANNELS.find(c => c[0] === k) || ['', '', 'mail'])[2];
export const channelTone = k => (CHANNELS.find(c => c[0] === k) || ['', '', '', 'g-slate'])[3];

/** Pill colour per channel, matching the tone above. */
export const channelPill = k =>
  k === 'email' ? 'p-blue' : k === 'whatsapp' ? 'p-green' : k === 'sms' ? 'p-amber' : 'p-purple';

/** Placeholders a template can use; substituted per recipient before sending. */
export const MERGE = [
  ['{{name}}', 'Account or person name'],
  ['{{first}}', 'First name of the contact'],
  ['{{company}}', 'Company name'],
  ['{{plan}}', 'Plan they are on or discussing'],
  ['{{amount}}', 'Amount due or deal value'],
  ['{{days}}', 'Days left, or days overdue'],
  ['{{owner}}', 'Who owns the account'],
  ['{{promo}}', 'Their promo code'],
  ['{{contact}}', 'Contact person']
];

/*
 * The ones filled from the client ledger.
 *
 * A marketing contact has a name, an address and a company and nothing else -
 * no plan, no balance, no renewal date - so these come out empty for them and
 * leave a sentence like "your  plan renews in  days". The editor greys them
 * rather than hiding them: they are right for a campaign to clients, and
 * wrong for one to the marketing list, and which it is depends on the
 * audience picked two steps later.
 */
export const LEDGER_MERGE = ['{{plan}}', '{{amount}}', '{{days}}', '{{promo}}'];

/** Who qualifies for an audience. '' means everyone in the source. */
export const AUDIENCE_FLAGS = [
  ['', 'Everyone in the source'],
  ['trial-soon', 'Trial ending within 3 days'],
  ['trial-over', 'Trial ended, unpaid'],
  ['unpaid', 'Payment not received'],
  ['overdue', 'Payment overdue'],
  ['renewing', 'Renewal within 30 days'],
  ['stale', 'Gone quiet'],
  ['upsell', 'No add-ons yet'],
  ['churned', 'Churned or expired'],
  ['free', 'On the free plan'],
  ['open-deal', 'Has an open deal'],
  ['won', 'Deal won'],
  ['new-lead', 'New lead, never worked']
];

/** Flags that only make sense against the client ledger. */
export const CLIENT_ONLY_FLAGS = [
  'trial-soon', 'trial-over', 'unpaid', 'overdue', 'renewing', 'stale', 'upsell', 'churned', 'free'
];

/** Flags that only make sense against contacts and their deals. */
export const CONTACT_ONLY_FLAGS = ['open-deal', 'won', 'new-lead'];

/** [type, label, icon] */
export const STEP_TYPES = [
  ['message', 'Send a message', 'send'],
  ['wait', 'Wait', 'wait'],
  ['task', 'Create a task', 'check'],
  ['notify', 'Notify the owner', 'bell'],
  ['exit', 'Leave if…', 'split']
];

export const EXITS = [
  ['paid', 'they pay'],
  ['replied', 'they reply'],
  ['converted', 'they become a client'],
  ['optout', 'they opt out']
];

/** The AI Studio's tools. */
export const STUDIO_TOOLS = [
  ['sequence', 'Build a sequence', 'flow'],
  ['audience', 'Find an audience', 'target'],
  ['subjects', 'Subject line variants', 'mail'],
  ['rewrite', 'Rewrite a message', 'wand'],
  ['grade', 'Grade a message', 'check'],
  ['reply', 'Triage a reply', 'chat']
];

export const STUDIO_HINTS = {
  sequence: 'Describe the outcome you want. It reads your book, picks the audience, and writes every step.',
  audience: 'Describe who you want to reach in plain English. It works out the filters and creates the segment.',
  subjects: 'Paste a subject line or describe the campaign. You get five variants with a note on why each might win.',
  rewrite: 'Paste a message and choose what to change — shorter, warmer, blunter, or in Arabic.',
  grade: 'Paste a message. It checks length, clarity, spam triggers and gives a predicted reply rate with fixes.',
  reply: 'Paste a reply you received. It classifies the intent and drafts your response.'
};

export const REWRITE_MODES = [
  ['shorter', 'Shorter and tighter'],
  ['warmer', 'Warmer and more personal'],
  ['blunter', 'Blunter and more direct'],
  ['formal', 'More formal'],
  ['arabic', 'Arabic'],
  ['bilingual', 'English then Arabic']
];

export const CAMPAIGN_STATUSES = ['Draft', 'Scheduled', 'Sending', 'Sent', 'Paused'];

/* Goals worth one click. The field stays free text underneath, so a campaign
   written before this list existed keeps whatever it says. Out here rather
   than in the wizard because the filter bar offers the same three. */
export const GOALS = [
  ['Promotion', 'tag'],
  ['Engagement', 'chat'],
  ['Retention', 'star']
];

/* ---------------------------------------------------- marketing contacts */

/**
 * Marketing keeps its own address book, separate from the sales contact list.
 *
 * The two answer different questions: a sales contact is somebody a deal is
 * being worked with, owned by a rep and moving through a pipeline. A marketing
 * contact is only ever an address to reach — a list bought, imported, or typed
 * in for one campaign — and putting those into the pipeline made the sales
 * screens unusable. They never mix: the campaign wizard reads this list, and
 * nothing here appears under Sales.
 */
export const MKT_CONTACT_STATUSES = ['Subscribed', 'Unconfirmed', 'Bounced', 'Unsubscribed'];

/** Where a marketing contact came from. Free text underneath. */
export const MKT_CONTACT_SOURCES = ['Import', 'Manual', 'Campaign', 'Website', 'Event', 'Referral'];

/** Column names an imported file may use, mapped to what a contact stores. */
export const MKT_IMPORT_HEADERS = {
  name: 'name', fullname: 'name', contact: 'name', contactperson: 'name', person: 'name',
  email: 'email', emailaddress: 'email', mail: 'email', 'e-mail': 'email',
  phone: 'phone', mobile: 'phone', whatsapp: 'phone', number: 'phone', contactnumber: 'phone',
  company: 'company', organisation: 'company', organization: 'company', business: 'company',
  notes: 'notes', note: 'notes', remark: 'notes'
};

/* ------------------------------------------------------- proven sequences */

/**
 * Sequences worth having ready, so a new journey does not start from nothing.
 * The keys are what the "Use a proven sequence" picker offers.
 */
export const PRESETS = {
  trial: {
    name: 'Trial nurture',
    steps: [
      { type: 'message', channel: 'email', subject: 'Welcome to {{plan}}, {{first}}', body: 'Hi {{first}},\n\nYour {{plan}} trial is live. The fastest win is importing last month — most people are through it in twenty minutes.\n\nReply with a question any time.\n\n{{owner}}' },
      { type: 'wait', days: 6 },
      { type: 'message', channel: 'whatsapp', body: 'Hi {{first}} — halfway through the {{plan}} trial. Anything stuck? Happy to jump on a quick call.' },
      { type: 'wait', days: 5 },
      { type: 'exit', when: 'paid' },
      { type: 'message', channel: 'email', subject: '{{days}} days left on your trial', body: 'Hi {{first}},\n\n{{days}} days left. Everything you have set up carries over — nothing to redo.\n\n{{plan}} is {{amount}} for your cycle. Say the word and I will send the link.\n\n{{owner}}' },
      { type: 'wait', days: 2 },
      { type: 'task', title: 'Call {{name}} — trial ends tomorrow' }
    ]
  },
  dunning: {
    name: 'Payment recovery',
    steps: [
      { type: 'message', channel: 'email', subject: '{{amount}} due in {{days}} days', body: 'Hi {{first}},\n\nA quick heads-up that {{amount}} for {{company}} is due in {{days}} days. Nothing to do if it is already scheduled.\n\n{{owner}}' },
      { type: 'wait', days: 4 },
      { type: 'exit', when: 'paid' },
      { type: 'message', channel: 'whatsapp', body: 'Hi {{first}}, {{amount}} on the {{company}} account is now due. Shall I resend the invoice?' },
      { type: 'wait', days: 3 },
      { type: 'exit', when: 'paid' },
      { type: 'notify' },
      { type: 'task', title: 'Call {{name}} about {{amount}} overdue' }
    ]
  },
  onboarding: {
    name: 'New client onboarding',
    steps: [
      { type: 'message', channel: 'email', subject: 'Welcome aboard, {{first}}', body: 'Hi {{first}},\n\nDelighted to have {{company}} on {{plan}}. I have put your setup call options below — pick whichever suits.\n\n{{owner}}' },
      { type: 'wait', days: 3 },
      { type: 'task', title: 'Setup call with {{name}}' },
      { type: 'wait', days: 11 },
      { type: 'message', channel: 'email', subject: 'Two weeks in — how is it going?', body: 'Hi {{first}},\n\nTwo weeks on {{plan}}. Anything awkward? I would rather fix it now than at month end.\n\n{{owner}}' }
    ]
  },
  winback: {
    name: 'Win back',
    steps: [
      { type: 'message', channel: 'email', subject: 'Anything we could have done better?', body: 'Hi {{first}},\n\n{{company}} left us a while back and I would rather learn why than guess. One line is plenty.\n\n{{owner}}' },
      { type: 'wait', days: 7 },
      { type: 'message', channel: 'whatsapp', body: 'Hi {{first}} — no sales pitch, just curious what made {{company}} move on. It genuinely helps.' },
      { type: 'wait', days: 14 },
      { type: 'message', channel: 'email', subject: 'A few things have changed', body: 'Hi {{first}},\n\nSome of what frustrated you has since been fixed. If you fancy another look, the first month is on us.\n\n{{owner}}' }
    ]
  },
  upsell: {
    name: 'Add-on upsell',
    steps: [
      { type: 'message', channel: 'email', subject: '{{first}}, what most {{plan}} accounts add next', body: 'Hi {{first}},\n\nAccounts like {{company}} usually add Inventory or CFO once the basics are humming.\n\nWant me to switch one on for a fortnight so you can judge it properly?\n\n{{owner}}' },
      { type: 'wait', days: 6 },
      { type: 'exit', when: 'converted' },
      { type: 'message', channel: 'whatsapp', body: 'Hi {{first}} — offer still stands on a two-week trial of an add-on for {{company}}. Worth a look?' }
    ]
  }
};

/* ------------------------------------------------------------------ seed */

/** Audiences, templates and campaigns for an empty marketing workspace. */
export function buildMarketingSeed(formatDate) {
  const today = formatDate(new Date());

  const audiences = [
    { id: 'g1', name: 'Trials ending in 3 days', src: 'clients', flag: 'trial-soon' },
    { id: 'g2', name: 'Trials that lapsed unpaid', src: 'clients', flag: 'trial-over' },
    { id: 'g3', name: 'Payments overdue', src: 'clients', flag: 'overdue' },
    { id: 'g4', name: 'Renewals coming up', src: 'clients', flag: 'renewing' },
    { id: 'g5', name: 'Free plan — upgrade candidates', src: 'clients', flag: 'free' },
    { id: 'g6', name: 'No add-ons yet', src: 'clients', flag: 'upsell' },
    { id: 'g7', name: 'Gone quiet for 30 days', src: 'clients', flag: 'stale' },
    { id: 'g8', name: 'Churned — win back', src: 'clients', flag: 'churned' },
    { id: 'g9', name: 'New leads, never worked', src: 'contacts', flag: 'new-lead' },
    { id: 'g10', name: 'Leads with an open deal', src: 'contacts', flag: 'open-deal' }
  ];

  const templates = [
    {
      id: 't1', name: 'Trial ending — convert', channel: 'email',
      subject: '{{first}}, your XYRA trial ends in {{days}} days',
      body: 'Hi {{first}},\n\nYour {{plan}} trial at {{company}} ends in {{days}} days. Everything you have set up stays exactly as it is once you move over — nothing to redo.\n\n{{plan}} works out at {{amount}} for your cycle. Reply here and I will send the payment link, or we can jump on a ten-minute call if anything is still unclear.\n\nBest,\n{{owner}}'
    },
    {
      id: 't2', name: 'Payment overdue — gentle chase', channel: 'whatsapp', subject: '',
      body: 'Hi {{first}}, {{amount}} on the {{company}} account is {{days}} days past due. If it has already gone out, ignore this. If not, I can resend the invoice — shall I?'
    },
    {
      id: 't3', name: 'Renewal coming up', channel: 'email',
      subject: '{{company}} renews in {{days}} days',
      body: 'Hi {{first}},\n\nJust a heads-up that {{company}} renews in {{days}} days on {{plan}} at {{amount}}.\n\nNothing to do if you are happy — it continues as is. If you want to change plan, add a module, or move to annual and save 20%, tell me before the date and I will sort it.\n\nBest,\n{{owner}}'
    },
    {
      id: 't4', name: 'Free plan — upgrade nudge', channel: 'email',
      subject: '{{first}}, ready for more than the free plan?',
      body: 'Hi {{first}},\n\nYou have been on the free plan for a while now — good sign it is doing the job.\n\nSTARTER at AED 49 a month adds the things people usually hit next. Happy to walk you through what changes, or just enable it for you today.\n\nBest,\n{{owner}}'
    },
    {
      id: 't5', name: 'Add-on upsell', channel: 'whatsapp', subject: '',
      body: 'Hi {{first}} — the accounts on {{plan}} like yours mostly add Inventory or CFO next. Want me to switch one on for a fortnight so you can see if it earns its keep?'
    },
    {
      id: 't6', name: 'Win back a churned account', channel: 'email',
      subject: 'Anything we could have done better, {{first}}?',
      body: 'Hi {{first}},\n\n{{company}} left us a while back and I would rather learn why than guess. One line is plenty.\n\nIf it was something we have since fixed, I will happily set you up again with the first month on us.\n\nBest,\n{{owner}}'
    },
    {
      id: 't7', name: 'Payment reminder — SMS', channel: 'sms', subject: '',
      body: '{{company}}: {{amount}} is due in {{days}} days. Pay or query: reply to this message. Reply STOP to opt out.'
    },
    {
      id: 't8', name: 'New feature announcement', channel: 'push',
      subject: 'New in XYRA',
      body: 'E-sign is live — send and sign engagement letters without leaving your ledger.'
    }
  ];

  /*
   * Marketing's own list. These are addresses, not leads: nobody here is in
   * the pipeline, which is exactly the point of keeping the two apart.
   */
  const contacts = [
    { id: 'mc1', name: 'Aisha Rahman', email: 'aisha@almasagroup.ae', phone: '+971 50 442 1180', company: 'Al Masa Group', status: 'Subscribed', source: 'Event', notes: '', created: today },
    { id: 'mc2', name: 'Rohit Menon', email: 'rohit@gulfsteeltrading.ae', phone: '+971 55 330 9042', company: 'Gulf Steel Trading', status: 'Subscribed', source: 'Website', notes: '', created: today },
    { id: 'mc3', name: 'Fatima Al Suwaidi', email: 'fatima@nooraccounting.ae', phone: '+971 52 118 7734', company: 'Noor Accounting', status: 'Subscribed', source: 'Referral', notes: '', created: today },
    { id: 'mc4', name: 'Daniel Okafor', email: 'daniel@brightlogistics.ae', phone: '+971 56 901 2277', company: 'Bright Logistics', status: 'Unconfirmed', source: 'Import', notes: '', created: today },
    { id: 'mc5', name: 'Priya Nair', email: 'priya@zenithinteriors.ae', phone: '+971 50 776 3391', company: 'Zenith Interiors', status: 'Subscribed', source: 'Event', notes: '', created: today },
    { id: 'mc6', name: 'Khalid Bin Omar', email: 'khalid@dunesconsult.ae', phone: '+971 54 220 8815', company: 'Dunes Consulting', status: 'Subscribed', source: 'Website', notes: '', created: today },
    { id: 'mc7', name: 'Grace Fernandes', email: 'grace@harbourcafe.ae', phone: '+971 58 664 5502', company: 'Harbour Cafe', status: 'Bounced', source: 'Import', notes: 'Address bounced twice', created: today },
    { id: 'mc8', name: 'Sami Haddad', email: 'sami@orbitmedia.ae', phone: '+971 50 993 4418', company: 'Orbit Media', status: 'Subscribed', source: 'Manual', notes: '', created: today }
  ];

  const campaigns = [
    {
      id: 'k1', name: 'August trial conversions', channel: 'email', audience: 'g1', status: 'Draft',
      goal: 'Convert trials before they lapse', subject: templates[0].subject, body: templates[0].body,
      created: today, sends: []
    },
    {
      id: 'k2', name: 'Overdue collections push', channel: 'whatsapp', audience: 'g3', status: 'Draft',
      goal: 'Collect what is past due', subject: '', body: templates[1].body, created: today, sends: []
    },
    {
      id: 'k3', name: 'Free to STARTER', channel: 'email', audience: 'g5', status: 'Draft',
      goal: 'Move free accounts onto a paid plan', subject: templates[3].subject, body: templates[3].body,
      created: today, sends: []
    }
  ];

  const journeys = [
    {
      id: 'jy1', name: 'Trial nurture', audience: 'g1', enabled: false, recool: 90,
      exitOnLeave: true, enrolled: {}, created: today, steps: PRESETS.trial.steps
    },
    {
      id: 'jy2', name: 'Payment recovery', audience: 'g3', enabled: false, recool: 60,
      exitOnLeave: true, enrolled: {}, created: today, steps: PRESETS.dunning.steps
    },
    {
      id: 'jy3', name: 'Win back the churned', audience: 'g8', enabled: false, recool: 180,
      exitOnLeave: false, enrolled: {}, created: today, steps: PRESETS.winback.steps
    }
  ];

  return {
    campaigns, audiences, templates, journeys, contacts,
    queue: [], optouts: [], consent: {}
  };
}

export const EMPTY_MARKETING = {
  campaigns: [], audiences: [], templates: [], journeys: [], contacts: [],
  queue: [], optouts: [], consent: {}
};
